import { isVehicleLocationType } from 'src/constants/pillarBoundaries'
import { IConfiguration, IPhoto, Maybe } from 'src/types/graphql'

/**
 * Product-aware photo ordering for a location's gallery (search-card carousel
 * + listing-page gallery).
 *
 * WHY: a location with both storage and parking configurations shares ONE
 * location-level photo pool, so the parking URL / parking card used to swipe
 * through the storage cages (DECSA Centro, parking config 6c744e — see
 * spotme-server/src/scripts/hostOnboarding/configs/decsaInnerTower.ts).
 *
 * WHAT LINKS A PHOTO TO A PRODUCT: only `Configuration.coverPhotoId`. `Photo`
 * carries no config/type tag and there is no per-config photo array, so the
 * rest of the pool cannot be attributed to a product. The rule is therefore
 * deliberately minimal:
 *   1. the lead photo (card: the server-resolved cover; listing page: the
 *      active configuration's own `coverPhotoId`) comes first, once;
 *   2. then the pool in host order, minus photos that are the cover of a
 *      configuration of the OTHER product family (vehicle vs. storage, via
 *      `isVehicleLocationType`) and of no same-family configuration;
 *   3. those other-product covers go last.
 * Step 2/3 only runs for a MIXED location (both families among
 * `configurations`), so a single-product listing keeps its host order.
 *
 * Pure + deterministic (no window, no Date) — safe to call during SSR.
 */

export type GalleryConfigRef = Pick<
  IConfiguration,
  'id' | 'locationType' | 'coverPhotoId'
>

export interface GalleryOrderInput {
  photos: Maybe<IPhoto[]> | undefined
  /**
   * Photo the surface must lead with. Search cards pass the server-resolved,
   * config-aware `location.coverPhoto` (bandit arm → config cover → location
   * cover, location.resolver.ts) — that selection is not re-derived here.
   */
  cover?: Maybe<IPhoto>
  /** The configuration the page/card is about. */
  activeConfiguration?: Maybe<GalleryConfigRef>
  /**
   * Every configuration of the location. Omit when the surface does not
   * fetch them (search cards): mixed-product demotion is then skipped.
   */
  configurations?: Maybe<GalleryConfigRef[]>
}

/** Same image: by id when both sides carry one (listing page), else by url
 *  (card fragments fetch `photos { url }` only). */
const isSamePhoto = (a: IPhoto, b: IPhoto): boolean => {
  if (a.id && b.id) return a.id === b.id
  return !!a.url && a.url === b.url
}

const isMixedProduct = (configurations: GalleryConfigRef[]): boolean => {
  const families = new Set(
    configurations.map((c) => isVehicleLocationType(c.locationType))
  )
  return families.size > 1
}

/** Photo ids that are a cover of the other product family only. */
const otherProductCoverIds = (
  configurations: GalleryConfigRef[],
  activeIsVehicle: boolean
): Set<string> => {
  const sameFamily = new Set<string>()
  const otherFamily = new Set<string>()
  configurations.forEach(({ coverPhotoId, locationType }) => {
    if (!coverPhotoId) return
    const bucket =
      isVehicleLocationType(locationType) === activeIsVehicle
        ? sameFamily
        : otherFamily
    bucket.add(coverPhotoId)
  })
  return new Set([...otherFamily].filter((id) => !sameFamily.has(id)))
}

export function orderGalleryPhotos({
  photos,
  cover,
  activeConfiguration,
  configurations
}: GalleryOrderInput): IPhoto[] {
  const pool = photos ?? []
  const configs = configurations ?? []
  const isMixed = !!activeConfiguration && isMixedProduct(configs)

  const ownCoverId = isMixed ? activeConfiguration?.coverPhotoId : undefined
  const lead =
    cover ??
    (ownCoverId ? pool.find((photo) => photo.id === ownCoverId) : undefined)

  if (!lead && !isMixed) return [...pool]

  const rest = lead ? pool.filter((photo) => !isSamePhoto(photo, lead)) : pool
  if (!isMixed || !activeConfiguration) {
    return lead ? [lead, ...rest] : [...rest]
  }

  const demotedIds = otherProductCoverIds(
    configs,
    isVehicleLocationType(activeConfiguration.locationType)
  )
  const isDemoted = (photo: IPhoto): boolean => demotedIds.has(photo.id)
  const ordered = [
    ...rest.filter((photo) => !isDemoted(photo)),
    ...rest.filter(isDemoted)
  ]
  return lead ? [lead, ...ordered] : ordered
}
